import {
  Box,
  Flex,
  Input,
  Text,
  Button as ChakraButton,
  VStack,
  HStack,
  useColorModeValue,
  IconButton,
  Tag,
  TagLabel,
  TagCloseButton,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import colors from "../../utils/colors";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useCreateNotification, useUpdateDoctor } from "../../apis/doctor";
import { EditIcon } from "@chakra-ui/icons";
import customToast, { ToastType } from "../../components/Toast";
import { TDoctorInfo } from "../../apis/types";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setDoctorInfo } from "../../store/authSlice";
import Dropdown from "../../components/ui/dropdown";
import { specialties } from "../patients/utils";

interface DropdownOption {
  label: string;
  value: string;
}

const MedicalLicenseCard = () => {
  const { doctorInfo } = useAppSelector((state) => state.auth);

  const userId = doctorInfo?._id;
  const dispatch = useAppDispatch();
  const { mutate: updateDoctor, isPending } = useUpdateDoctor();
  const [isEditing, setIsEditing] = useState(false);
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null);
  const { mutate: createNotification } = useCreateNotification();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textColor = useColorModeValue(colors.dark, "gray.100");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const labelColor = useColorModeValue(colors.dark, "gray.200");
  const dropdownInputStyles =
    "!p-[8px] !rounded-[8px] !text-[14px] !border !border-gray-300";
  const licenses = doctorInfo?.licenseDocuments ?? [];

  const formik = useFormik<{
    licenseNumber: string;
    specialty: string[];

    file: File | null;
  }>({
    initialValues: {
      licenseNumber: doctorInfo?.licenseNumber || "",
      specialty: doctorInfo?.specialty || [],

      file: null,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      licenseNumber: yup
        .string()
        .required("Practicing license number is required"),
      specialty: yup
        .array()
        .of(yup.string().required("Specialty name is required"))
        .min(1, "At least one specialty is required")
        .required("Specialty is required"),

      file: yup.mixed().nullable(),
    }),
    onSubmit: async (values) => {
      createNotification({
        userId: doctorInfo?._id,
        title: "Profile Updated",
        message: "Doctor's medical license was successfully updated.",
      });
      const updatedData: Partial<TDoctorInfo> = {
        licenseNumber: values.licenseNumber,
        specialty: values.specialty,
      };

      updateDoctor(
        {
          data: updatedData,
          doctorId: doctorInfo?._id,
        },
        {
          onSuccess: async (apiResponse: any) => {
            customToast("Medical license updated!", ToastType.success);

            let newDoctorInfo = {
              ...doctorInfo,
              ...updatedData,
              licenseDocument: doctorInfo.licenseDocuments, // preserve existing if no new upload
            } as TDoctorInfo;

            // if (values.file instanceof File) {
            //   try {
            //     const formData = new FormData();
            //     formData.append("file", values.file);
            //     const res = await fetch(
            //       `${process.env.REACT_APP_BASE_URL}/doctor/${doctorInfo?._id}/media?tag=license`,
            //       {
            //         method: "POST",
            //         headers: {
            //           Authorization: `Bearer ${localStorage.getItem(
            //             "access_token"
            //           )}`,
            //         },
            //         body: formData,
            //       }
            //     );
            //     if (res.ok) {
            //       const mediaData = await res.json();
            //       // if (mediaData?.data?.url) {
            //       //   newDoctorInfo.licenseDocument = mediaData.data.url;
            //       // }
            //       if (mediaData?.data?.url) {
            //         // Append to array instead of replacing
            //         newDoctorInfo.licenseDocuments = [
            //           ...(doctorInfo.licenseDocuments || []),
            //           mediaData.data.url,
            //         ];
            //       }
            //       customToast("License document uploaded.", ToastType.success);
            //     } else {
            //       const errorData = await res
            //         .json()
            //         .catch(() => ({ message: "License upload failed." }));
            //       customToast(
            //         errorData.message || "License upload failed.",
            //         ToastType.error
            //       );
            //     }
            //   } catch (err) {
            //     customToast("Error uploading license.", ToastType.error);
            //     console.error("Error uploading license:", err);
            //   }
            // }
            if (values.file instanceof File) {
              try {
                const formData = new FormData();
                formData.append("file", values.file);

                // 👇 include replaceIndex in query if replacing
                let url = `${process.env.REACT_APP_BASE_URL}/doctor/${doctorInfo?._id}/media?tag=license`;
                if (replaceIndex !== null) {
                  url += `&replaceIndex=${replaceIndex}`;
                }

                const res = await fetch(url, {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "access_token"
                    )}`,
                  },
                  body: formData,
                });

                if (res.ok) {
                  const mediaData = await res.json();
                  if (mediaData?.data?.url) {
                    let newDocs = [...(doctorInfo.licenseDocuments || [])];
                    if (replaceIndex !== null && newDocs[replaceIndex]) {
                      // Replace existing
                      newDocs[replaceIndex] = mediaData.data.url;
                    } else {
                      // Add new
                      newDocs.push(mediaData.data.url);
                    }
                    newDoctorInfo.licenseDocuments = newDocs;
                  }
                  customToast("License document uploaded.", ToastType.success);
                } else {
                  const errorData = await res
                    .json()
                    .catch(() => ({ message: "License upload failed." }));
                  customToast(
                    errorData.message || "License upload failed.",
                    ToastType.error
                  );
                }
              } catch (err) {
                customToast("Error uploading license.", ToastType.error);
              } finally {
                setReplaceIndex(null); // 👈 reset after action
              }
            }
            dispatch(setDoctorInfo(newDoctorInfo));
            const sessionDoc = sessionStorage.getItem("created-doctor");
            if (sessionDoc) {
              try {
                const parsedSessionDoc = JSON.parse(sessionDoc) as TDoctorInfo;
                const updatedSessionDoc = {
                  ...parsedSessionDoc,
                  ...newDoctorInfo,
                };
                sessionStorage.setItem(
                  "created-doctor",
                  JSON.stringify(updatedSessionDoc)
                );
              } catch (e) {
                console.error(
                  "Error updating session storage for medical license",
                  e
                );
              }
            }
            setIsEditing(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
          },
          onError: (error: any) => {
            customToast(
              error?.response?.data?.message || "Update failed.",
              ToastType.error
            );
          },
        }
      );
    },
  });

  const hasChanges =
    formik.values.licenseNumber !== formik.initialValues.licenseNumber ||
    JSON.stringify(formik.values.specialty) !==
      JSON.stringify(formik.initialValues.specialty) ||
    formik.values.file !== null;

  const handleCancelEdit = () => {
    formik.resetForm();
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsEditing(false);
  };

  let dropdownDisplayValue = "";
  const numSelectedSpecialties = formik.values.specialty.length;
  if (numSelectedSpecialties === 1) {
    dropdownDisplayValue = formik.values.specialty[0];
  } else if (numSelectedSpecialties > 1) {
    dropdownDisplayValue = `${numSelectedSpecialties} specialties selected`;
  }

  if (!isEditing) {
    return (
      <Box>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text
            fontFamily="lato"
            fontWeight="bold"
            fontSize="xl"
            color={textColor}
          >
            Medical License
          </Text>
          <IconButton
            aria-label="Edit medical license"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            onClick={() => setIsEditing(true)}
          />
        </Flex>
        <VStack spacing={3} align="start">
          <HStack>
            <Text fontWeight="medium" color={labelColor} minW="150px">
              Practicing License Number:
            </Text>
            <Text color={subtleTextColor}>
              {doctorInfo?.licenseNumber || "N/A"}
            </Text>
          </HStack>
          <HStack>
            <Text fontWeight="medium" color={labelColor} minW="150px">
              Specialties:
            </Text>
            <Text color={subtleTextColor}>
              {doctorInfo?.specialty?.join(", ") || "N/A"}
            </Text>
          </HStack>

          {/* Show all uploaded licenses */}
          {licenses?.length > 0 && (
            <VStack align="start" spacing={2}>
              <Text fontWeight="medium" color={labelColor} minW="150px">
                Documents:
              </Text>
              {doctorInfo.licenseDocuments?.map((docUrl, idx) => (
                <HStack key={idx} spacing={3}>
                  <ChakraButton
                    as="a"
                    href={docUrl}
                    target="_blank"
                    variant="link"
                    colorScheme="blue"
                    size="sm"
                  >
                    View License {idx + 1}
                  </ChakraButton>
                  <ChakraButton
                    size="xs"
                    colorScheme="orange"
                    onClick={() => {
                      setIsEditing(true);
                      setReplaceIndex(idx); // 👈 tell form which license is being replaced
                    }}
                  >
                    Replace
                  </ChakraButton>
                </HStack>
              ))}
            </VStack>
          )}
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text
          fontFamily="lato"
          fontWeight="bold"
          fontSize="xl"
          color={textColor}
        >
          Edit Medical License
        </Text>
      </Flex>
      <form onSubmit={formik.handleSubmit}>
        <VStack spacing={4} align="stretch">
          <Box>
            <Text
              fontFamily="lato"
              fontWeight={400}
              fontSize="sm"
              color={labelColor}
              mb="8px"
            >
              Practicing License Number
            </Text>
            <Input
              name="licenseNumber"
              value={formik.values.licenseNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.licenseNumber && !!formik.errors.licenseNumber
              }
              placeholder="Enter practicing license number"
              size="sm"
            />
            {formik.touched.licenseNumber && formik.errors.licenseNumber && (
              <Text color="red.500" fontSize="xs" mt={1}>
                {formik.errors.licenseNumber}
              </Text>
            )}
          </Box>

          <Box>
            <Text
              fontFamily="lato"
              fontWeight={400}
              fontSize="sm"
              color={labelColor}
              mb="8px"
            >
              Specialty
            </Text>
            <Dropdown
              options={specialties}
              value={dropdownDisplayValue}
              onSelect={(selectedValues: string[] | string | null) => {
                if (Array.isArray(selectedValues)) {
                  formik.setFieldValue("specialty", selectedValues);
                } else if (typeof selectedValues === "string") {
                  formik.setFieldValue("specialty", [selectedValues]);
                } else {
                  formik.setFieldValue("specialty", []);
                }
              }}
              placeholder="Select specialties"
              isMulti={true}
              inputStyles={dropdownInputStyles}
            />
            {formik.values.specialty.length > 0 && (
              <Flex mt={2} wrap="wrap" gap={2}>
                {formik.values.specialty.map((spec, index) => (
                  <Tag
                    size="md"
                    key={index}
                    borderRadius="full"
                    variant="solid"
                    colorScheme="blue"
                  >
                    <TagLabel>{spec}</TagLabel>
                    <TagCloseButton
                      onClick={() => {
                        const newSpecialties = formik.values.specialty.filter(
                          (s) => s !== spec
                        );
                        formik.setFieldValue("specialty", newSpecialties);
                      }}
                    />
                  </Tag>
                ))}
              </Flex>
            )}
            {formik.touched.specialty && formik.errors.specialty && (
              <Text color="red.500" fontSize="xs" mt={1}>
                {typeof formik.errors.specialty === "string"
                  ? formik.errors.specialty
                  : Array.isArray(formik.errors.specialty)
                  ? formik.errors.specialty.join(", ")
                  : "Specialty is required"}
              </Text>
            )}
          </Box>

          <Box>
            <Text
              fontFamily="lato"
              fontWeight={400}
              fontSize="sm"
              color={labelColor}
              mb="8px"
            >
              {replaceIndex !== null
                ? `Replace License ${replaceIndex + 1}`
                : "Upload New License Document (Optional)"}
            </Text>

            <Input
              type="file"
              ref={fileInputRef}
              accept="image/*,.pdf"
              onChange={(e) =>
                formik.setFieldValue("file", e.target.files?.[0] || null)
              }
              isDisabled={isPending}
              size="sm"
              pt={1.5}
            />
            {formik.values.file && (
              <Text fontSize="xs" mt={1} color={subtleTextColor}>
                Selected: {formik.values.file.name}
              </Text>
            )}
          </Box>

          <Flex gap={3} justifyContent="flex-end" mt={4}>
            <ChakraButton
              variant="ghost"
              onClick={handleCancelEdit}
              size="sm"
              isDisabled={isPending}
            >
              Cancel
            </ChakraButton>

            <ChakraButton
              bg={colors.primary}
              color={colors.white}
              _hover={{ bg: colors.secondaryBlack || colors.primary }}
              type="submit"
              size="sm"
              isLoading={isPending}
              isDisabled={isPending || !hasChanges || !formik.isValid}
            >
              Save Changes
            </ChakraButton>
          </Flex>
        </VStack>
      </form>
    </Box>
  );
};

export default MedicalLicenseCard;
